import { Order } from '@@types'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { useMutateSaveFeedback } from '@entities/Order/api/saveFeedback.gql'
import { useMutateUpdateReviewInvite } from '@entities/Order/api/updateReviewInvite.gql'

import { useModalStore } from '@features/modal'
import { uploadFile } from '@features/upload'

import Button from '@shared/ui/button'
import Checkbox from '@shared/ui/checkbox'
import ErrorMessage from '@shared/ui/error-message'
import RatingStars from '@shared/ui/rating/stars'
import Textarea from '@shared/ui/text-area'
import { useToastStore } from '@shared/ui/toast/useToastStore'
import UploadFile from '@shared/ui/upload-file'

import styles from './styles.module.scss'

const feedbackSchema = z.object({
  rating: z.number().min(1, 'grade_required'),
  text: z.string().optional(),
  needCall: z.boolean(),
  files: z.array(z.number()).optional(),
})

type FeedbackFormData = z.infer<typeof feedbackSchema>

export interface OrderRatingModalProps {
  order: Order
  onSuccess?: () => void
}

export default function OrderRatingModal (props: OrderRatingModalProps) {
  const { order, onSuccess } = props
  const [ fetchSaveFeedback, { loading } ] = useMutateSaveFeedback()
  const [ updateReviewInvite ] = useMutateUpdateReviewInvite()
  const { closeAllModals } = useModalStore()
  const addToast = useToastStore((state) => state.addToast)
  const { t: to } = useTranslation('order')
  const { t: tc } = useTranslation('common')

  const [ uploadedIds, setUploadedIds ] = useState<number[]>([])

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      rating: 0,
      text: '',
      needCall: false,
      files: [],
    },
  })

  const handleFileAccepted = async (allFiles: File[]) => {
    // Загружаем только новые файлы
    const newFiles = allFiles.slice(uploadedIds.length)
    if (newFiles.length === 0) return

    try {
      const uploaded = await Promise.all(
        newFiles.map((file) => uploadFile('feedback-image', file)),
      )
      const newIds = uploaded.map((f) => f.id)
      const allIds = [ ...uploadedIds, ...newIds ]
      setUploadedIds(allIds)
      setValue('files', allIds, { shouldValidate: true, shouldDirty: true })
    }
    catch (_err) {
      addToast({
        variant: 'error',
        message: tc('error_upload_files'),
      })
    }
  }

  const onSubmit = async (data: FeedbackFormData) => {
    try {
      // Помечаем, что пользователь ответил на приглашение оценить заказ
      await updateReviewInvite({
        variables: {
          order_id: order.id,
          reviewInvite: true,
        },
      })

      const { data: feedbackData } = await fetchSaveFeedback({
        variables: {
          feedbackInfo: {
            order_id: order.id,
            brand_id: order.shop.brand_id,
            review: data.text || to('feedback.noComment'),
            general_rating: data.rating,
            need_call: data.needCall,
            images: uploadedIds,
          },
        },
      })

      if (feedbackData?.saveFeedback?.id) {
        addToast({
          variant: 'success',
          message: to('feedback.successDesc'),
        })
        closeAllModals()
        onSuccess?.()
      }
    }
    catch (_err) {
      addToast({
        variant: 'error',
        message: to('feedback.error'),
      })
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.root}>
      <div className={styles.root__rating}>
        <h5>{to('feedback.rate')}</h5>
        <Controller
          name="rating"
          control={control}
          render={({ field }) => (
            <RatingStars
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        {errors.rating && (
          <ErrorMessage>{tc(errors.rating.message as string)}</ErrorMessage>
        )}
      </div>

      <div className={styles.root__comment}>
        <h5>{to('feedback.comment')}</h5>
        <Controller
          name="text"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              placeholder={to('feedback.commentPlaceholder')}
              rows={4}
            />
          )}
        />
      </div>

      <div className={styles.root__photos}>
        <h5>{to('feedback.photos')}</h5>
        <UploadFile
          accept={[ 'image/*' ]}
          onFileAccepted={handleFileAccepted}
        />
      </div>

      <div className={styles.root__needCall}>
        <Controller
          name="needCall"
          control={control}
          render={({ field }) => (
            <Checkbox
              checked={field.value}
              onChange={field.onChange}
              label={to('feedback.needCall')}
            />
          )}
        />
      </div>

      <div className={styles.root__actions}>
        <Button
          type="button"
          theme="grey"
          onClick={closeAllModals}
          disabled={loading}
        >
          {tc('cancel')}
        </Button>
        <Button type="submit" loading={loading}>
          {to('feedback.submit')}
        </Button>
      </div>
    </form>
  )
}
